/* The house chip: a small neutral pill that MARKS a fact beside the thing it is about.
 *
 * Worn today by the trainer home's session signal chips — mood, skipped sets, weight up and down,
 * records — which add exactly two declarations of their own (`pages/trainer-home/ui/
 * feedbackRow.module.css`): the gap between a mark and its words, and the ink on the figure.
 *
 * It was written for the plan library's "Własnym tempem" marker and named after it. Neither that
 * table nor the roster imports it, so the name described an intended consumer rather than the form;
 * renamed 16 September 2026 on its first real one.
 *
 * ── It is content, never a label ────────────────────────────────────────────────────────────
 * The chip renders as real text beside its subject, so it JOINS the row's accessible name instead
 * of replacing it. An `aria-label` carrying the mark would drop what the row actually says, and
 * 2.5.3 with it.
 *
 * ── Neutral, and that is the decision ───────────────────────────────────────────────────────
 * The accent is a scalpel and every screen that wears this already spends its accent on a real
 * action. Meaning is carried by the WORD and, where there is one, the glyph — never by the fill.
 *
 * `flex: none` and `white-space: nowrap`: it sits next to a name that ellipsises, and the chip must
 * be the thing that survives the squeeze — a marker clipped to „Własnym tem…" says less than none.
 *
 * ── The FORM is the plan library's category chip, minus its wash ────────────────────────────
 * `--r-pill`, `--fs-micro`, the same 24px height and the same 11px inset, so no host grows a third
 * pill shape. No dot either: the category chip's dot carries its colour scale a second time, and
 * there is no scale here to carry.
 *
 * Contrast, computed from the resolved token values rather than eyeballed: `--text-2` on
 * `--bg-chip` is **5.87:1 in Flame** (`--ink-600` on `--stone-150`) and **8.25:1 in Flame Dark**.
 * AA on both, and this is the pairing the standing `--text-3` finding warns about. */
.neutralChip {
  display: inline-flex;
  flex: none;
  align-items: center;
  height: var(--space-6);
  padding: var(--space-0) var(--space-11px);
  font-size: var(--fs-micro);
  font-weight: var(--fw-bold);
  line-height: var(--lh-auto);
  white-space: nowrap;
  color: var(--text-2);
  background: var(--bg-chip);
  border-radius: var(--r-pill);
}

/* The compact cut: 20px, a 6px corner and the semibold step, for a chip that stands in a dense
 * table cell beside other chips — the exercise library's „Twoje" and „Do uzupełnienia" statuses
 * (4 November 2026). Same ink and fill; the default above is unchanged for every other host. */
.small {
  height: 20px;
  padding: var(--space-0) var(--space-2);
  font-weight: var(--fw-semibold);
  border-radius: var(--radius-xs);
}
